import { AllPermissions } from '@/Network/Types/Responses/AuthorizationResponses'

export interface AuthorizationRouteMeta {
  authorization?: {
    permission: AllPermissions
    resourceType: string
  }
}

export enum AuthorizationResourcesAndSubjects {
  Platform = 'platform',
  Company = 'company',
  Group = 'group',
  HealthUnit = 'health_unit',
  Schedule = 'schedule',
  DsAttendance = 'ds_attendance',
  DsAttendanceCompany = 'ds_attendance_company',
  DsAttendanceHealthUnit = 'ds_attendance_health_unit',
  StationExceptionalitiesCompany = 'station_exceptionalities_company',
  StationExceptionalitiesHealthUnit = 'station_exceptionalities_health_unit',
  StationExceptionalitiesSchedule = 'station_exceptionalities_schedule',
  User = 'user',
  Position = 'position',
  NpsCompany = 'nps_company',
  NpsHealthUnit = 'nps_health_unit',
  PostStationCompany = 'post_station_company',
  PostStationHealthUnit = 'post_station_health_unit'
}

export enum AuthorizationPermissions {
  // PlatformPermissions
  IsSuperAdministrator = 'is_super_administrator',
  SuperAdminstratorAccessing = 'super_adminstrator_accessing',
  // Gestão › Afiliados e "ver como": super admin + visualizador restrito (antecipe_affiliate_viewer)
  CanViewAntecipeAffiliates = 'can_view_antecipe_affiliates',

  // Ds Policys
  CompanyPolicyToggleActivationGranted = 'company_policy_toggle_activation_granted',
  CompanyPolicyLinksEditingGranted = 'company_policy_links_editing_granted',
  CompanyPolicyCreatingGranted = 'company_policy_creating_granted',

  // CompanyPermissions
  AccessLevelEditing = 'access_level_editing_grant_access',
  AccessLevelGranting = 'access_level_granting_grant_access',
  AccessLevelRemoving = 'access_level_removing_grant_access',
  SharingModalReading = 'sharing_modal_reading_grant_access',
  CompanyUserCreating = 'company_user_creating',

  // HealthUnitPermissions
  HealthUnitPolicyLinksEditing = 'health_unit_policy_links_editing',
  HealthUnitCreating = 'health_unit_creating',
  HealthUnitEditing = 'health_unit_editing',
  HealthUnitDeactivating = 'health_unit_deactivating',
  HealthUnitBasicReading = 'health_unit_basic_reading',
  HealthUnitReordering = 'health_unit_reordering',

  // SchedulePermissions
  SchedulePolicyLinksEditing = 'schedule_policy_links_editing',
  SchedulePaymentPoliciesCreating = 'schedule_payment_policies_creating',
  SchedulePaymentPoliciesEditing = 'schedule_payment_policies_editing',
  SchedulePaymentPoliciesDeactivating = 'schedule_payment_policies_deactivating',
  ScheduleFormationCreating = 'schedule_formation_creating',
  ScheduleFormationEditing = 'schedule_formation_editing',
  ScheduleFormationDeactivating = 'schedule_formation_deactivating',
  SchedulePositionCreating = 'schedule_position_creating',
  SchedulePositionEditing = 'schedule_position_editing',
  SchedulePositionDeactivating = 'schedule_position_deactivating',
  ScheduleShiftCreating = 'schedule_shift_creating',
  ScheduleShiftEditing = 'schedule_shift_editing',
  ScheduleShiftDeactivating = 'schedule_shift_deactivating',
  ScheduleCreating = 'schedule_creating',
  ScheduleEditing = 'schedule_editing',
  ScheduleDeactivating = 'schedule_deactivating',
  StationExceptionalitiesListReading = 'station_exceptionalities_list_reading',
  SchedulePaymentPoliciesLinksReading = 'schedule_payment_policies_links_reading',
  SchedulePaymentPoliciesReading = 'schedule_payment_policies_reading',
  ScheduleProportionalityRuleReading = 'schedule_proportionality_rule_reading',
  ScheduleRequerimentReading = 'schedule_requeriment_reading',
  ScheduleBasicReading = 'schedule_basic_reading',
  StationExceptionalitiesReverting = 'station_exceptionalities_reverting',
  StationExceptionalitiesApproving = 'station_exceptionalities_approving',
  StationExceptionalitiesReproving = 'station_exceptionalities_reproving',
  StationObservationsDeleting = 'station_observations_deleting',
  StationObservationsEditing = 'station_observations_editing',
  StationDeleting = 'station_deleting',
  StationChecksEvidencesManaging = 'station_checks_evidences_managing',
  StationCreating = 'station_creating',
  CompanyDoctorInviteCreating = 'company_doctor_invite_creating',
  StationDoctorPackageRemoving = 'station_doctor_package_removing',
  StationDoctorPackageReplacing = 'station_doctor_package_replacing',
  StationDoctorPackageAllocating = 'station_doctor_package_allocating',
  StationExceptionalitiesCancelling = 'station_exceptionalities_cancelling',
  StationExceptionalitiesRequesting = 'station_exceptionalities_requesting',
  StationObservationsCreating = 'station_observations_creating',
  StationEditing = 'station_editing',
  StationExceptionalitiesReading = 'station_exceptionalities_reading',
  StationObservationsReading = 'station_observations_reading',
  StationAntecipeReading = 'station_antecipe_reading',
  StationPaymentRulesReading = 'station_payment_rules_reading',
  StationEditionHistoricalReading = 'station_edition_historical_reading',
  StationReportsExtraction = 'station_reports_extraction',
  StationDoctorCheckReading = 'station_doctor_check_reading',
  StationBiometryPhotoReading = 'station_biometry_photo_reading',
  StationBasicReading = 'station_basic_reading',
  StationDoctorCheckXlsReportExporting = 'station_doctor_check_xls_report_exporting',
  StationPdfReportExporting = 'station_pdf_report_exporting',
  StationXlsReportExporting = 'station_xls_report_exporting',
  StationExceptionalitiesFlowConfiguring = 'flow_configuring',
  StationExceptionalitiesExceptionValidating = 'exception_validating',
  StationExceptionalitiesExceptionViewing = 'exception_viewing',
  // Post-Station (Pós-Plantão) no domínio Gestão de Escalas — leituras exclusivas do schedule.
  // As 3 compartilhadas (panel_reading, approval_reverting, manual_revalidating) ficam no bloco PostStationPermissions.
  PostStationDomainDetailReading = 'post_station_domain_detail_reading',
  PostStationHistoryReading = 'post_station_history_reading',
  PostStationStationStatusReading = 'post_station_station_status_reading',
  PostStationQualificationDocumentalReading = 'post_station_qualification_documental_reading',
  PostStationQualificationTransferReading = 'post_station_qualification_transfer_reading',

  // DsAttendancePermissions
  AttendanceConfiguring = 'attendance_configuring',
  AttendanceEmitting = 'attendance_emitting',
  AttendanceBasicReceiving = 'attendance_basic_receiving',
  AttendanceAdvancedReceiving = 'attendance_advanced_receiving',
  AttendanceExtracting = 'attendance_extracting',
  AttendanceGeneralAdministrating = 'attendance_general_administrating',
  // F2/CONFIG-02 (Q31) — editar a seção de registro e auditoria da política
  // (Registro de Atividade, precedência das fontes, bloqueio do pós-plantão).
  // Mais restrita que `AttendanceConfiguring`.
  AttendanceRegistrationConfiguring = 'attendance_registration_configuring',
  // Perfil Extrator — uniões do schema.zed (backend): o extrator é ortogonal à
  // cadeia emissor ⊃ receptor e foi criado para baixar as folhas.
  // Leitura da fila, do detalhe e da folha: emissor+ ou extrator (sem escrita).
  AttendanceReading = 'attendance_reading',
  // Export em lote das folhas (`containers/export`): receptor básico+ ou extrator.
  AttendanceSheetExporting = 'attendance_sheet_exporting',

  // NpsPermissions
  NpsFormCreating = 'nps_form_creating',
  NpsFormEditing = 'nps_form_editing',
  NpsFormCloning = 'nps_form_cloning',
  NpsFormMoving = 'nps_form_moving',
  NpsEvaluatorManaging = 'nps_evaluator_managing',
  NpsSettingsManaging = 'nps_settings_managing',
  NpsAuditExporting = 'nps_audit_exporting',
  NpsTechnicalEvaluating = 'nps_technical_evaluating',
  NpsPatientNpsReading = 'nps_patient_nps_reading',
  NpsInstitutionalEvaluating = 'nps_institutional_evaluating',
  NpsOwnHistoryReading = 'nps_own_history_reading',
  NpsDashboardReading = 'nps_dashboard_reading',
  NpsRankingExporting = 'nps_ranking_exporting',

  // PostStationPermissions (domínio Pós-Plantão — definitions post_station_company/post_station_health_unit)
  PostStationPanelReading = 'post_station_panel_reading',
  PostStationApprovalReverting = 'post_station_approval_reverting',
  PostStationManualRevalidating = 'post_station_manual_revalidating',
  QualificationDocumentalPolicyReading = 'qualification_documental_policy_reading',
  QualificationDocumentalPolicyCreating = 'qualification_documental_policy_creating',
  QualificationDocumentalPolicyEditing = 'qualification_documental_policy_editing',
  QualificationDocumentalPolicyToggling = 'qualification_documental_policy_toggling',
  QualificationDocumentalPolicyLinking = 'qualification_documental_policy_linking',
  QualificationDocumentalDoctorsManaging = 'qualification_documental_doctors_managing',
  QualificationDocumentalBulkImporting = 'qualification_documental_bulk_importing',
  QualificationTransferPolicyReading = 'qualification_transfer_policy_reading',
  QualificationTransferPolicyCreating = 'qualification_transfer_policy_creating',
  QualificationTransferPolicyEditing = 'qualification_transfer_policy_editing',
  QualificationTransferPolicyToggling = 'qualification_transfer_policy_toggling',
  QualificationTransferPolicyLinking = 'qualification_transfer_policy_linking',
  QualificationTransferDoctorsManaging = 'qualification_transfer_doctors_managing',
  QualificationTransferBulkImporting = 'qualification_transfer_bulk_importing'
}

export enum AuthorizationRelations {
  Platform = 'platform',
  Parent = 'parent',
  GeneralAdmin = 'general_administrator',
  ContentAdmin = 'content_administrator',
  IntermediateEditor = 'intermediate_editor',
  BasicEditor = 'basic_editor',
  AdvancedReader = 'advanced_reader',
  IntermediateReader = 'intermediate_reader',
  BasicReader = 'basic_reader',
  DirectMember = 'direct_member',
  Member = 'member',
  Emitter = 'emitter',
  BasicReceiver = 'basic_receiver',
  AdvancedReceiver = 'advanced_receiver',
  Extractor = 'extractor',
  QualityAdmin = 'quality_admin',
  TechnicalEvaluator = 'technical_evaluator',
  InstitutionalEvaluator = 'institutional_evaluator',
  AnalyticsViewer = 'analytics_viewer',
  Operator = 'operator',
  MedicalCoordinator = 'medical_coordinator',
  AuditAnalyst = 'audit_analyst',
  TechnicalValidator = 'technical_validator',
  StrategicValidator = 'strategic_validator',
  ExecutiveValidator = 'executive_validator',
  DocumentationAnalyst = 'documentation_analyst',
  FinancialAnalyst = 'financial_analyst'
}

export type AuthorizationSubjects = {
  id?: number
  resource_relation?: AuthorizationRelations
  subject_id?: number
  subject_type: AuthorizationResourcesAndSubjects
  isDelete?: boolean
}

export type AuthorizationResource = {
  objectType: AuthorizationResourcesAndSubjects
  objectId: number
}

export type Authorization = {
  resourceType: AuthorizationResourcesAndSubjects
  permission: AuthorizationPermissions
}

export type AuthorizationPermissionCheck = {
  resource: AuthorizationResource
  subject?: AuthorizationSubjects
  permission: AuthorizationPermissions
}

export type Authorizations = Authorization[]

export type PermissionImpactResponse = {
  subject_id: number
  subject_type: string
  resource_id: number
  resource_type: string
  resource_name?: string
  current_relation: string
}
